// One-shot SFX player. Web Audio API for low-latency, gap-free
// retriggering — important because most of these fire on rapid taps
// (button presses) and HTMLAudioElement re-plays clip themselves on
// rapid repeats.
//
// Usage:
//   import { playSfx } from "../lib/sfx";
//   playSfx("button-press");
//
// Preloads + decodes all five files on first call. Browsers block
// AudioContext until a user gesture; the first playSfx call usually
// IS a gesture, so it works the first time. If somehow called before
// any gesture (programmatic mount), the play queues quietly and fires
// once the context resumes.

const AudioCtxCtor =
  typeof window !== "undefined"
    ? window.AudioContext || window.webkitAudioContext
    : null;

const SFX_FILES = {
  "button-press":       "/mp3/sfx/button-press.mp3",
  "open-drill-down":    "/mp3/sfx/open-drill-down.mp3",
  "paper-flipping":     "/mp3/sfx/paper-flipping-recipe-book.mp3",
  "receipt-open":       "/mp3/sfx/receipt-sound-open.mp3",
  "shopping-cart-open": "/mp3/sfx/shopping-cart-sound-open.mp3",
  "radio-on":           "/mp3/sfx/radio-on.mp3",
  "radio-off":          "/mp3/sfx/radio-off.mp3",
  "notification-bell":  "/mp3/sfx/notification-bell.mp3",
  "kitchen-tab":        "/mp3/sfx/kitchen-tab.mp3",
  "home-tab":           "/mp3/sfx/home-tab.mp3",
};

const VOLUME = 0.5;

// Per-sfx volume multipliers, applied AFTER the global VOLUME bus.
// Use sparingly — only when a specific clip's mastered loudness sits
// above or below the rest of the SFX library. 1.0 is the default
// (full bus level); 0.5 halves; 1.5 boosts.
const VOLUME_OVERRIDES = {
  "home-tab": 0.35,   // mastered hot — pulled down ~9 dB
};

let ctx = null;
let gainNode = null;
const buffers = new Map(); // name → AudioBuffer
let initPromise = null;

function ensureCtx() {
  if (ctx || !AudioCtxCtor) return ctx;
  ctx = new AudioCtxCtor();
  gainNode = ctx.createGain();
  gainNode.gain.value = VOLUME;
  gainNode.connect(ctx.destination);
  return ctx;
}

function ensureLoaded() {
  if (initPromise) return initPromise;
  if (!AudioCtxCtor) return Promise.resolve();
  initPromise = (async () => {
    ensureCtx();
    await Promise.all(
      Object.entries(SFX_FILES).map(async ([name, url]) => {
        try {
          const res = await fetch(url);
          const arr = await res.arrayBuffer();
          const buf = await new Promise((resolve, reject) =>
            ctx.decodeAudioData(arr, resolve, reject),
          );
          buffers.set(name, buf);
        } catch (err) {
          console.warn(`[sfx] failed to load ${name}:`, err);
        }
      }),
    );
  })();
  return initPromise;
}

function fireBuffer(buf, name) {
  if (!ctx || !buf) return;
  const source = ctx.createBufferSource();
  source.buffer = buf;
  const override = VOLUME_OVERRIDES[name];
  if (override != null && override !== 1) {
    // One-shot gain node so the override applies only to THIS source —
    // other sfx through the same shared gainNode stay unaffected.
    const trim = ctx.createGain();
    trim.gain.value = override;
    source.connect(trim);
    trim.connect(gainNode);
  } else {
    source.connect(gainNode);
  }
  source.start();
}

export function playSfx(name) {
  if (!AudioCtxCtor) return;
  ensureCtx();
  const fire = () => {
    const buf = buffers.get(name);
    if (!buf) return;
    if (ctx.state === "suspended") {
      ctx.resume().then(() => fireBuffer(buf, name)).catch(() => {});
    } else {
      fireBuffer(buf, name);
    }
  };
  if (buffers.has(name)) {
    fire();
  } else {
    ensureLoaded().then(fire).catch(() => {});
  }
}

// Preload at app startup so the first SFX firing has zero load
// latency. Call from App's mount effect.
export function preloadSfx() {
  ensureLoaded().catch(() => {});
}
